import {isRecurring} from "./components/task-recurrence-model";
import React, {useEffect, useState} from 'react';
import PageHeaderButton from "../components/page-header-button";
import {Button} from "primereact/button";
import TaskDialog from "./task-dialog";
import {useTranslate} from "../../system/useTranslate";
import {TabPanel, TabView} from "primereact/tabview";
import Icon from "../../system/components/icon";
import {BreadCrumb} from "primereact/breadcrumb";
import {useNavigate} from "react-router-dom";
import {useTask} from "../../models/task/use-task";
import {useDispatch, useSelector} from "react-redux";
import TasksBoard from "./tabs/tasks-board";
import Tasks from "./tabs/tasks";
import Dashboard from "./tabs/dashboard";
import Calendar from "./tabs/calendar";
import Can from "../../system/can";
import {mergeWorkspaceTasks, workspaceGraphSelectors, upsertWorkspaceTask} from "../../system/workspace-graph-slice";
import SelectableTabsButton, {useSelectableTabs} from "../components/selectable-tabs-button";
import {filterMyTasksScope} from "../../models/task/my-tasks-scope";
import {usePermission} from "../../system/usePermission";
import {usePublishAiScope} from "../../system/ai-scope-context";
import MeetingSidebarHost from "../projects/meetings/meeting-sidebar";


function Index(props) {
    const [taskDialogVisible, setTaskDialogVisible] = useState();
    const [activeIndex,setActiveIndex] = useState(0);
    const [breadCrumb,setBreadCrumbData] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [taskCreationVersion, setTaskCreationVersion] = useState(0);
    const task = useTask();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const workspaceTasks = useSelector(workspaceGraphSelectors.tasks);
    const translate = useTranslate();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const permission = usePermission();
    const tabs = [
        { id: "list", label: "list", icon: "mi mi:list", view:Tasks },
        { id: "board", label: "board", icon: "mi mi:view_kanban", view:TasksBoard },
        { id: "dashboard", label: "dashboard", icon: "mi mi:dashboard", view:Dashboard },
        { id: "calendar", label: "calendar", icon: "mi mi:calendar_month", view:Calendar },
    ];
    const {visibleTabs, selectedIds, toggleTab} = useSelectableTabs(tabs, "visible-tabs:tasks", activeIndex, setActiveIndex);
    usePublishAiScope({
        activeTab: visibleTabs[activeIndex]?.id,
        selectedEntityIds: tasks.map((item) => item.documentId || item.id),
        filters: {scopeSelectionProvided: true},
    });
    useEffect(()=>{
        setBreadCrumbData([
            {label:translate("my-tasks"), command:()=>{navigate("/tasks")}}
        ]);
    },[]);

    const createTask = async (taskData, subTasks=[])=>{
        if (!permission.can("canManageTasks", true)) return;
        if (taskData){
            // The series template is captured when recurrence is created, so a recurring task with subtasks gets its
            // recurrence after the subtasks exist (every occurrence then copies them).
            const deferredRecurrence = subTasks.length && isRecurring(taskData.recurrence) ? taskData.recurrence : null;
            taskData = {...taskData, workspace:workspaceMembershipData.workspace.id}
            if (deferredRecurrence) delete taskData.recurrence;
            let parent = await task.createTask(taskData);
            dispatch(upsertWorkspaceTask(parent));
            await Promise.all(
                subTasks.map(st =>{
                    delete st?.id;
                    return task.createTask({
                        ...st,
                        parentTask: parent.id,
                        startAt: Number.isInteger(st.startAt) ? new Date(st.startAt * 1000) : st.startAt,
                        dueDate: Number.isInteger(st.dueDate) ? new Date(st.dueDate * 1000) : st.dueDate,
                        workspace: workspaceMembershipData.workspace.id,
                    }).then((created) => {
                        dispatch(upsertWorkspaceTask(created));
                        return created;
                    });
                })
            );
            if (deferredRecurrence) {
                const withRecurrence = await task.updateTask(parent.documentId, {recurrence: deferredRecurrence});
                if (withRecurrence) dispatch(upsertWorkspaceTask(withRecurrence));
            }
            // workspaceGraph is the single source of truth. Both the create response
            // and realtime can arrive for the same task, and its upsert is idempotent.
            setTaskCreationVersion((current) => current + 1);
            return parent;
        }
    }
    const updateTask = async (taskData, updateData, subTasks=[])=>{
        if (!permission.can("canManageTasks", true)) return;

        let updatedTask = null;
        if (updateData){
            updatedTask = await task.updateTask(taskData.documentId, updateData);
            if (updatedTask) dispatch(upsertWorkspaceTask(updatedTask));
        }
        const _subTasks = await Promise.all(
            subTasks.map(st =>{
                delete st?.id;

                if (st?.documentId){
                    let documentId = st?.documentId;
                    delete st?.documentId;
                    delete st?.createdAt;
                    delete st?.updatedAt;
                    // console.log(st);
                    return task.updateTask(documentId, {...st, dueDate: st.dueDate?(Number.isInteger(st.dueDate)?new Date(st.dueDate*1000).toISOString():st.dueDate):undefined}).then((updated) => {
                        dispatch(upsertWorkspaceTask(updated));
                        return updated;
                    });
                }
                else {
                    // console.log(st);
                    return task.createTask({...st, parentTask: taskData.id, dueDate: st.dueDate?new Date(st.dueDate*1000).toISOString():st.dueDate, workspace:workspaceMembershipData.workspace.id}).then((created) => {
                        dispatch(upsertWorkspaceTask(created));
                        return created;
                    });
                }
            })
        );

        setTasks((current) => {
            let nextTasks = [...current];

            // a deleted (archived) task leaves the list at once
            if (updatedTask && (updateData?.archivedAt || updatedTask.archivedAt)) {
                nextTasks = nextTasks.filter((item) => item.documentId !== updatedTask.documentId && item.id !== updatedTask.id);
            } else if (updatedTask) {
                const updatedIndex = nextTasks.findIndex((item) => item.documentId === updatedTask.documentId || item.id === updatedTask.id);
                if (updatedIndex >= 0) {
                    nextTasks[updatedIndex] = {...nextTasks[updatedIndex], ...updatedTask};
                } else {
                    nextTasks.push(updatedTask);
                }
            }

            _subTasks.filter(Boolean).forEach((subTask) => {
                const subTaskIndex = nextTasks.findIndex((item) => item.documentId === subTask.documentId || item.id === subTask.id);
                if (subTaskIndex >= 0) {
                    nextTasks[subTaskIndex] = {...nextTasks[subTaskIndex], ...subTask};
                } else {
                    nextTasks.push(subTask);
                }
            });

            return filterMyTasksScope(
                nextTasks,
                workspaceMembershipData?.workspace,
                workspaceMembershipData,
            );
        });
        // dispatch(setMessage({severity:"success", content:translate("task-updated")}))
    }

    useEffect(()=>{
        if (workspaceMembershipData?.workspace?.id){
            getTasks();
        }
    },[workspaceMembershipData?.workspace?.id, workspaceMembershipData?.documentId])

    useEffect(() => {
        setTasks(filterMyTasksScope(
            workspaceTasks,
            workspaceMembershipData?.workspace,
            workspaceMembershipData,
        ));
    }, [
        workspaceTasks,
        workspaceMembershipData?.id,
        workspaceMembershipData?.documentId,
        workspaceMembershipData?.workspace?.id,
        workspaceMembershipData?.workspace?.documentId,
    ]);

    const getTasks = ()=>{
        (async () => {
            const [_tasks] = await Promise.all([
                task.getTasks(null, workspaceMembershipData?.workspace.id, {scope: "my-tasks"}),
            ]);
            setTasks(_tasks);
            dispatch(mergeWorkspaceTasks(_tasks));
        })();
    }


    return (
        <div className="h-full overflow-hidden flex flex-column">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={breadCrumb}/>
            <PageHeaderButton title={props.name} description={props.description} end={ <Can permission="canManageTasks"><Button onClick={()=>{setTaskDialogVisible(true)}} icon={<Icon icon="mi mi:add" className="text-lg ml-1"/>} className="text-xs" label={translate("create-task")}/></Can>} />
            {/* تب‌ها */}
            <div className="flex-1 min-h-0 flex flex-column relative">
                <SelectableTabsButton tabs={tabs} selectedIds={selectedIds} onChange={toggleTab} className="absolute z-5" style={{top: ".45rem", left: ".75rem"}}/>
                <TabView lazy activeIndex={activeIndex} onTabChange={(e)=>{setActiveIndex(e.index);
                        setBreadCrumbData(prv=>{
                            const next = Array.isArray(prv) ? [...prv] : [];
                            next[1] = {label:translate(visibleTabs[e.index].label)};
                            return next;
                        });
                    }}
                    panelContainerClassName="flex-1  min-h-0 p-0 border-noround"
                    className="h-full flex flex-column">
                    {visibleTabs.map((tab, tabIndex) => {
                        const TabContent = tab.view;
                        return (
                        <TabPanel
                            key={tab.id}
                            header={translate(tab.label)}
                            leftIcon={<Icon icon={tab.icon} className="pl-2" />}
                            className="h-full text-xs">
                            {tabIndex === activeIndex && <div className="h-full min-h-0 overflow-hidden">
                                <TabContent tasks={tasks} setTasks={setTasks} createTask={createTask} updateTask={updateTask} resetFiltersKey={taskCreationVersion} />
                            </div>}
                        </TabPanel>
                    )})}
                </TabView>
            </div>
            <TaskDialog setVisible={setTaskDialogVisible} visible={taskDialogVisible} onSubmit={(e, subtasks)=>{createTask(e, subtasks); setTaskDialogVisible(false)}}/>
            {/* the meeting sidebar: opened by a meeting card and by «?meeting=<key>» of a notification (docs/tasks/30-meeting-cards.md) */}
            <MeetingSidebarHost/>
        </div>
    );
}


export default Index;
